import { Card } from "antd";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useLanguageStore } from "../../../stores/languageStore";

const formatPercent = (value, digits = 1) =>
  `${(Number(value || 0) * 100).toFixed(digits)}%`;

const normalizePercentage = (value) => {
  const numberValue = Number(value || 0);

  return numberValue > 1 ? numberValue / 100 : numberValue;
};

const GENDER_COLORS = ["#F9A43F", "#FFBF69", "#FFDCA8"];
const renderPercentLabel = ({ percent }) => `${(percent * 100).toFixed(1)}%`;
const CHART_INITIAL_DIMENSION = { width: 1, height: 1 };

const GENDER_LABELS = {
  Male: "남성",
  Female: "여성",
  Other: "기타",
};

export const AudienceInsights = ({ influencerInfo }) => {
  const { lang } = useLanguageStore();

  const regionNames =
    typeof Intl !== "undefined" && Intl.DisplayNames
      ? new Intl.DisplayNames([lang], { type: "region" })
      : null;
  const englishRegionNames =
    typeof Intl !== "undefined" && Intl.DisplayNames
      ? new Intl.DisplayNames(["en"], { type: "region" })
      : null;

  const formatCountryLabel = (value) => {
    if (!value) return "-";

    const normalized = String(value).trim();
    if (normalized === "Others") return lang === "ko" ? "기타" : "Others";
    if (normalized === "Other") return lang === "ko" ? "기타" : "Other";

    if (/^[a-z]{2}$/i.test(normalized) && regionNames) {
      return regionNames.of(normalized.toUpperCase()) || normalized;
    }

    return normalized;
  };

  const formatCityLabel = (value) => {
    if (!value) return "-";

    const normalized = String(value).trim();
    if (!/^[a-z]{2}\s+/i.test(normalized)) {
      return normalized;
    }

    const countryCode = normalized.slice(0, 2);
    const cityName = normalized.slice(2).trim();
    const countryLabel =
      englishRegionNames?.of(countryCode.toUpperCase()) || countryCode;
    const normalizedCity =
      cityName === "Other" || cityName === "Others" ? "Other" : cityName;

    return `${normalizedCity} (${countryLabel})`;
  };

  const ageData = (influencerInfo?.audience_ages || []).map((item) => ({
    name: item.age,
    value: normalizePercentage(item.percentage),
  }));

  const genderData = (influencerInfo?.audience_genders || []).map((item) => ({
    name: item.gender,
    value: normalizePercentage(item.percentage),
  }));

  const countryData = (influencerInfo?.audience_countries || [])
    .map((item) => ({
      name: formatCountryLabel(item.country),
      value: normalizePercentage(item.percentage),
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 7);

  const citiesData = (influencerInfo?.audience_cities || [])
    .map((item) => ({
      name: formatCityLabel(item.city_name),
      value: normalizePercentage(item.percentage),
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 7);

  const maxCityValue = citiesData[0]?.value || 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-6 sm:flex-row">
        {ageData.length > 0 && (
          <Card
            title="연령대 분포"
            className="w-full min-w-0 rounded-xl border border-[#E8E8E8]"
            styles={{ body: { padding: "24px" } }}
          >
            <div className="audience-chart-wrap h-[275px] w-full min-w-0">
              <ResponsiveContainer
                width="100%"
                height="100%"
                initialDimension={CHART_INITIAL_DIMENSION}
              >
                <BarChart
                  data={ageData}
                  margin={{ top: 20, right: 12, left: 10, bottom: 0 }}
                >
                  <CartesianGrid stroke="#F3F4F6" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: "#666" }}
                    axisLine={{ stroke: "#D9D9D9" }}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={(value) => formatPercent(value, 1)}
                    tick={{ fontSize: 12, fill: "#666" }}
                    axisLine={false}
                    tickLine={false}
                    width={44}
                  />
                  <Tooltip
                    formatter={(value) => [formatPercent(value)]}
                    cursor={{ fill: "rgba(249, 164, 63, 0.08)" }}
                    contentStyle={{
                      borderRadius: "6px",
                      border: "1px solid #E8E8E8",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Bar
                    dataKey="value"
                    fill="#F9A43F"
                    radius={[6, 6, 0, 0]}
                    maxBarSize={48}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}

        {genderData.length > 0 && (
          <Card
            title="성별 분포"
            className="w-full min-w-0 rounded-xl border border-[#E8E8E8]"
            styles={{ body: { padding: "24px" } }}
          >
            <div className="space-y-5">
              <div className="flex flex-wrap items-center justify-center gap-4">
                {genderData.map((item, index) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{
                        backgroundColor:
                          GENDER_COLORS[index % GENDER_COLORS.length],
                      }}
                    />
                    <span className="text-[14px] font-medium text-[#262626]">
                      {GENDER_LABELS[item.name] || item.name}
                    </span>
                  </div>
                ))}
              </div>

              <div className="audience-chart-wrap mx-auto h-[240px] w-full min-w-0">
                <ResponsiveContainer
                  width="100%"
                  height="100%"
                  initialDimension={CHART_INITIAL_DIMENSION}
                >
                  <PieChart>
                    <Pie
                      data={genderData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={2}
                      label={renderPercentLabel}
                    >
                      {genderData.map((item, index) => (
                        <Cell
                          key={item.name}
                          fill={GENDER_COLORS[index % GENDER_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [formatPercent(value, 0)]}
                      contentStyle={{
                        borderRadius: "6px",
                        border: "1px solid #E8E8E8",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>
        )}
      </div>

      <div className="flex flex-col gap-6 sm:flex-row">
        {countryData.length > 0 && (
          <Card
            title="국가 분포"
            className="w-full rounded-xl border border-[#E8E8E8]"
            styles={{ body: { padding: "24px" } }}
          >
            <div className="audience-chart-wrap h-[290px] w-full min-w-0">
              <ResponsiveContainer
                width="100%"
                height="100%"
                initialDimension={CHART_INITIAL_DIMENSION}
              >
                <BarChart
                  data={countryData}
                  margin={{ top: 20, right: 12, left: 5, bottom: 0 }}
                >
                  <CartesianGrid stroke="#F3F4F6" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: "#666" }}
                    axisLine={{ stroke: "#D9D9D9" }}
                    tickLine={false}
                    interval={0}
                    angle={-10}
                  />
                  <YAxis
                    tickFormatter={(value) => formatPercent(value, 1)}
                    tick={{ fontSize: 12, fill: "#666" }}
                    axisLine={false}
                    tickLine={false}
                    width={44}
                  />
                  <Tooltip
                    formatter={(value) => [formatPercent(value)]}
                    cursor={{ fill: "rgba(249, 164, 63, 0.08)" }}
                    contentStyle={{
                      borderRadius: "6px",
                      border: "1px solid #E8E8E8",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Bar
                    dataKey="value"
                    fill="#F9A43F"
                    radius={[6, 6, 0, 0]}
                    maxBarSize={48}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}

        {citiesData.length > 0 && (
          <Card
            title="도시 분포"
            className="w-full rounded-xl border border-[#E8E8E8]"
            styles={{ body: { padding: "24px" } }}
          >
            <div className="max-h-[290px] overflow-y-auto space-y-3 pr-1">
              {citiesData.map((item, index) => {
                const widthPercent =
                  maxCityValue > 0 ? (item.value / maxCityValue) * 100 : 0;

                return (
                  <div
                    key={item.name}
                    className="rounded-xl border border-[#F1F1F1] bg-[#FAFAFA] px-4 py-3"
                  >
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="w-6 shrink-0 text-[13px] font-semibold text-[#8C8C8C]">
                          {index + 1}
                        </span>
                        <span className="truncate text-[14px] font-medium text-[#262626]">
                          {item.name}
                        </span>
                      </div>
                      <span className="shrink-0 text-[14px] text-[#262626] font-semibold">
                        {formatPercent(item.value, 2)}
                      </span>
                    </div>

                    <div className="h-2.5 overflow-hidden rounded-full bg-[#EDEDED]">
                      <div
                        className="h-full rounded-full bg-[#F9A43F]"
                        style={{ width: `${widthPercent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};
